import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { EwmRouterProvider } from '@assetmark/ewm30-routing-shell';
import { ElevationContextProvider } from '@assetmark/ui-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import jwtDecode from 'jwt-decode';
import { ErrorResponse, InMemoryWebStorage, UserManager, WebStorageStateStore, } from 'oidc-client-ts';
import { useEffect, useState } from 'react';
import { AuthProvider, hasAuthParams, useAuth, } from 'react-oidc-context';
import { Bat } from './Bat';
import { DefaultLayout } from './layouts/Default';
import { AuthenticationPendingPage } from './pages/AuthenticationPendingPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ewmAppRouteModuleResolver } from './routing/ewmAppRouteModuleResolver';
import { ewmAppRoutesCollector } from './routing/ewmAppRoutesCollector';
import { getOnSigninCallback } from './routing/signinCallback';
import { injectChatScript } from './injectChatScript';
const queryClient = new QueryClient();
// const PRELOAD_ADVISORINSIGHT = true // Enable/disable Advisor-insight First Tab preloading in webhost
const USE_DEFERRED_OIDC_REDIRECT = true;
const ROLE = {
    ADVISOR_REP_CODE: 'AdvisorRepCode',
    CLIENT_HOUSEHOLD: 'ClientHousehold',
};
const { url, realm, clientId, defaultIdpRedirect, keycloakOidc, notRedirectUrl, isBatEnabled, } = {
    url: process.env.KEYCLOAK_ENDPOINT,
    realm: 'eWM',
    clientId: 'eWMAuth',
    defaultIdpRedirect: process.env.DEFAULT_IDP_REDIRECT || '',
    keycloakOidc: process.env.KC_IDP_HINT || '',
    notRedirectUrl: process.env.NOT_AUTHORIZED_URL_REDIRECT || '',
    isBatEnabled: String(process.env.MFE_BAT_ENABLED)?.trim() == 'true',
};
const OIDC_CONFIG = {
    userManager: new UserManager({
        userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
        authority: `${url}/realms/${realm}`.replace(/([^:]\/)\/+/g, '$1') ?? '',
        client_id: clientId,
        // We cast BASE_URL to boolean in order to check if it is defined and not empty
        // Because it could be empty due to misconfiguration
        redirect_uri: window.BASE_URL
            ? `${window.BASE_URL}/signin-redirect`
            : `${window.location.origin}/signin-redirect`,
        silent_redirect_uri: window.BASE_URL
            ? `${window.BASE_URL}/silent-signin-redirect.html`
            : `${window.location.origin}/silent-signin-redirect.html`,
        extraQueryParams: defaultIdpRedirect === 'true' ? { kc_idp_hint: keycloakOidc } : {},
    }),
    onSigninCallback: getOnSigninCallback(USE_DEFERRED_OIDC_REDIRECT),
};
/**
 * As the whole application requires authentication, we wrap it with AuthProvider.
 * @constructor
 */
const AppInner = () => {
    const { isAuthenticated, activeNavigator, isLoading, signinSilent, signinRedirect, user, events, error, } = useAuth(); // Auth part
    const [isUserAuthorized, setIsUserAuthorized] = useState(false);
    const [isTryingToLogin, setIsTryingToLogin] = useState(false);
    const checkUserRole = (accessToken) => {
        const profile = jwtDecode(accessToken);
        if (!notRedirectUrl || notRedirectUrl?.length < 2) {
            console.log('✔ No redirect URL provided, user is authorized');
            setIsUserAuthorized(true);
        }
        else if (!profile?.orgs) {
            console.log('Orgs attribute not found, redirecting to not authorized page');
            location.replace(notRedirectUrl);
        }
        else {
            const isAdvisor = profile?.orgs.find((el) => {
                return el.split('|')[2] === ROLE.ADVISOR_REP_CODE;
            });
            if (!isAdvisor) {
                console.log('User does not have advisor role, redirecting to not authorized page');
                location.replace(notRedirectUrl);
            }
            else {
                console.log('✔ User has advisor role, user is authorized');
                setIsUserAuthorized(true);
            }
        }
    };
    if (!hasAuthParams() &&
        !isAuthenticated &&
        !activeNavigator &&
        !isLoading &&
        !isTryingToLogin) {
        signinSilent({
            redirect_uri: window.BASE_URL
                ? `${window.BASE_URL}/silent-signin-redirect.html`
                : `${window.location.origin}/silent-signin-redirect.html`,
            state: {
                href: window.location.href,
            },
            prompt: 'none',
        }).then((user) => {
            if (!user) {
                console.debug('[host] Redirecting user to SSO for sign in');
                signinRedirect({
                    state: {
                        href: window.location.href,
                    },
                });
                return;
            }
            console.debug('[host] User is authenticated.');
        }, (e) => {
            /**
             * Due to, what I believe is a bug in react-oidc-context, the silent signin
             * will never throw an error. Instead, it will resolve with a null.
             * See https://github.com/authts/react-oidc-context/issues/1176
             * - DB
             */
            console.error(e);
        });
        setIsTryingToLogin(true);
    }
    useEffect(() => {
        if (error) {
            console.error(error);
            if (error instanceof ErrorResponse) {
                if (error.error === 'login_required') {
                    console.debug('[host] Redirecting user to SSO for sign in');
                    signinRedirect({
                        state: {
                            href: window.location.href,
                        },
                    });
                }
            }
        }
    }, [error]);
    useEffect(() => {
        return events.addAccessTokenExpired(() => {
            console.debug('[host] Application missed token refresh window and has to restart sign in process');
            signinRedirect({
                state: {
                    href: window.location.href,
                },
            });
        });
    }, [events]);
    useEffect(() => {
        if (isAuthenticated && user?.access_token !== undefined) {
            console.log('user is authenticated', 'checking user role...');
            //----------------- Redirect if user doesn't have the advisor role
            checkUserRole(user?.access_token);
            //------------------ populate GB username
            const _detector = window._detector;
            if (_detector) {
                const profile = jwtDecode(user.access_token);
                const profileElement = profile['preferred_username'];
                _detector.trigger3rdPartyMap({
                    username: profileElement,
                });
            }
        }
    }, [isAuthenticated]);
    useEffect(() => {
        injectChatScript(user?.access_token);
    }, [user?.access_token]);
    // CHECKING THAT BAT IS ON DASHBOARD URL OR NOT
    const isDashboard = () => {
        return (window.location.pathname === '/' ||
            window.location.pathname === '/signin-redirect');
    };
    return (_jsx(_Fragment, { children: _jsx(QueryClientProvider, { client: queryClient, children: isUserAuthorized && (_jsxs(ElevationContextProvider, { children: [_jsx(EwmRouterProvider, { lazyRouteCollector: ewmAppRoutesCollector, routeModuleResolver: ewmAppRouteModuleResolver, ApplicationLayout: DefaultLayout, AuthenticationPendingPage: AuthenticationPendingPage, NotFoundPage: NotFoundPage }), isDashboard() && isBatEnabled && _jsx(Bat, {})] })) }) }));
};
const App = () => {
    return (_jsx(AuthProvider, { ...OIDC_CONFIG, children: _jsx(AppInner, {}) }));
};
export default App;
